import type { CaseType } from "../case-core";
import { type SystemTaskKey, SystemTaskKeySchema } from "../task";
import type { TaskAssignment } from "./assignment";

type FixedFlags = { automatedOnly: boolean; secondary: boolean; collectsCompletionNotes: boolean };

/** What code does with a code-owned task type; its fixed flags must match every task type with its key. */
type SystemTaskType = {
  fixed: FixedFlags;
  /** Routed by the intake gate from case facts; its task types carry no effects. */
  intake?: true;
  /** Resolution ids code or an AI prompt names by value; they must exist and stay active. */
  resolutions?: readonly string[];
  /** The only code-owned task types a completion may create it from; empty when only code creates it. */
  followsOnly?: readonly string[];
  analysis?: true;
  caseReview?: "liability_analysis" | "case_data";
  collects?: { requestedDocuments?: true; dvcSearchDetails?: true; observation?: true };
  /** Replaces the assignment modes of its context. */
  assignmentModes?: readonly TaskAssignment["mode"][];
  customerAssignable?: true;
  /** Anyone allowed to execute unowned tasks may complete it while unassigned. */
  unownedExecution?: true;
  excludeFromPreviousActivities?: true;
};

const caseTask = {
  automatedOnly: false,
  secondary: false,
  collectsCompletionNotes: true,
} as const;

/**
 * Behavior code attaches to a task type through its `system` key. Each case type has at most
 * one task type per key, and only case scopes use them. See specs/TASK_CATALOG.md for why
 * each of these stays in code.
 */
export const SYSTEM_TASK_TYPES = {
  review_case_creation: {
    fixed: { ...caseTask, automatedOnly: true, collectsCompletionNotes: false },
    intake: true,
    resolutions: ["completed"],
    followsOnly: [],
    excludeFromPreviousActivities: true,
  },
  provide_case_documents: {
    fixed: { ...caseTask, secondary: true },
    intake: true,
    resolutions: ["documents_provided", "documents_unavailable"],
    collects: { requestedDocuments: true, observation: true },
    assignmentModes: ["unassigned", "user"],
    customerAssignable: true,
  },
  review_case_exception: {
    fixed: { ...caseTask, automatedOnly: true },
    intake: true,
    resolutions: ["proceed", "reject_case"],
    followsOnly: [],
    unownedExecution: true,
  },
  review_missing_payment_receipt: {
    fixed: { ...caseTask, automatedOnly: true },
    unownedExecution: true,
  },
  analyze_case_liability: {
    fixed: caseTask,
    resolutions: ["fit_extrajudicial", "close_process"],
    analysis: true,
    caseReview: "liability_analysis",
  },
  analyze_debt_case: {
    fixed: caseTask,
    resolutions: ["fit_extrajudicial", "close_debt_process"],
    analysis: true,
    caseReview: "case_data",
  },
  search_dvc: {
    fixed: { ...caseTask, secondary: true },
    collects: { dvcSearchDetails: true },
  },
  update_third_party_contact: {
    fixed: { ...caseTask, automatedOnly: true, secondary: true, collectsCompletionNotes: false },
    followsOnly: ["search_dvc"],
    assignmentModes: ["unassigned", "user", "previous_assignee", "source_creator"],
  },
  request_additional_documents: {
    fixed: { ...caseTask, automatedOnly: true, secondary: true },
    collects: { requestedDocuments: true, observation: true },
  },
} as const satisfies Record<SystemTaskKey, SystemTaskType>;

/** What code does with a task type; undefined for task types defined only by data. */
export const systemTaskType = (
  system: SystemTaskKey | null | undefined,
): SystemTaskType | undefined => (system ? SYSTEM_TASK_TYPES[system] : undefined);

/** The code-owned keys whose behavior matches `predicate`. */
export const systemTasksWhere = (
  predicate: (behavior: SystemTaskType) => unknown,
): SystemTaskKey[] =>
  SystemTaskKeySchema.options.filter((system) => predicate(SYSTEM_TASK_TYPES[system]));

/** Code-owned task types that ask for case documents. */
export const DOCUMENT_COLLECTION_SYSTEM_TASKS = systemTasksWhere(
  (behavior) => behavior.collects?.requestedDocuments,
);

/** Code-owned task types with an AI analysis profile. */
export type AnalyzableSystemTask = {
  [Key in SystemTaskKey]: (typeof SYSTEM_TASK_TYPES)[Key] extends { analysis: true } ? Key : never;
}[SystemTaskKey];

/** Whether a task type has an AI analysis profile. */
export const isAnalyzableSystemTask = (
  system: SystemTaskKey | null | undefined,
): system is AnalyzableSystemTask => systemTaskType(system)?.analysis === true;

/** Whether completing a task of `source` can create a task of `target` from it alone. */
export const canFollow = (source: SystemTaskKey | null, target: SystemTaskKey | null): boolean => {
  const followsOnly = systemTaskType(target)?.followsOnly;
  return !followsOnly || (source !== null && followsOnly.includes(source));
};

/** The step the intake gate checks requirements for and the initial activity moves the case to. */
export const INTAKE_TARGET_STEP = "analysis";

/** The activity each case type starts with once intake lets it into analysis. */
export const CASE_INITIAL_ACTIVITY: Readonly<Record<CaseType, SystemTaskKey>> = {
  third_party_claim: "analyze_case_liability",
  road_conditions: "analyze_case_liability",
  zero_km_warranty: "analyze_case_liability",
  routine_maintenance: "analyze_case_liability",
  hidden_defect: "analyze_case_liability",
  debt_collection: "analyze_debt_case",
};
